import { DestroyRef, Injectable, NgZone, PLATFORM_ID, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

/**
 * Tracks the current local calendar day so date-display logic can recompute
 * exactly once per day instead of on every change-detection pass.
 *
 * `localDay` is the UTC millisecond timestamp of the start (local midnight) of
 * the current local day. It only changes when the local day rolls over, so a
 * pure pipe that receives it as an argument is re-evaluated only at that
 * moment — not on every change-detection pass.
 *
 * A single browser-only timer (one per app, since the service is a root
 * singleton) checks for a day change every minute. On the server (SSR) no
 * timer is started and the value is computed once, which is correct because a
 * server render is a single point in time.
 */
@Injectable({ providedIn: 'root' })
export class LocalDayService {
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly destroyRef = inject(DestroyRef);
  private readonly ngZone = inject(NgZone);

  /** Start-of-local-day (local midnight) in UTC ms for the current local day. */
  readonly localDay = signal<number>(this.startOfLocalDay(new Date()));

  constructor() {
    if (!this.isBrowser) {
      return;
    }

    // Check once a minute. The timer is created outside Angular's zone so the
    // per-minute callback does not trigger a change-detection pass; we only
    // re-enter the zone when the local day actually changes.
    this.ngZone.runOutsideAngular(() => {
      const intervalId = setInterval(() => this.tick(), 60_000);
      this.destroyRef.onDestroy(() => clearInterval(intervalId));
    });
  }

  /** Recomputes the local day and updates the signal if it changed. */
  private tick(): void {
    const next = this.startOfLocalDay(new Date());
    if (next !== this.localDay()) {
      // Re-enter Angular only on the (rare) day rollover so the signal update
      // is picked up by change detection.
      this.ngZone.run(() => this.localDay.set(next));
    }
  }

  /** Returns the UTC ms of local midnight for the given instant. */
  private startOfLocalDay(date: Date): number {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  }
}
